// The pure half of an OPD bill's fee lines — no React, so it can be unit
// tested (frontend/tests/opdLines.test.mjs). Used by OpdFeeLines.tsx.
import type { OpdFeeCategory, OpdFeeQuote, OpdFeeQuoteItem } from './types';

export interface OpdLine {
  key: string;
  itemId: number | null;
  category: OpdFeeCategory | null;
  description: string;
  fee: string;
  discount: string;
  gst: number;
}

export interface OpdBillItem {
  description: string;
  unit_price: number;
  hsn_code: string;
  gst_percent: number;
  discount_percent: number;
  quantity: number;
  opd_billing_item_id?: number;
}

let lineSeq = 0;
export const nextKey = () => `l${++lineSeq}`;

export function lineFromItem(item: OpdFeeQuoteItem): OpdLine {
  return {
    key: nextKey(), itemId: item.id, category: item.category, description: item.name,
    fee: String(item.fee), discount: '0', gst: item.gst_percent,
  };
}

/** The lines a new bill starts with: every item the schedule includes by default. */
export function initialLines(quote: OpdFeeQuote | undefined): OpdLine[] {
  return (quote?.items ?? []).filter((i) => i.include_by_default).map(lineFromItem);
}

/** The bill payload's items. Zero-value lines are dropped, as the old form did. */
export function toBillItems(lines: OpdLine[], quote: OpdFeeQuote | undefined, gstEnabled: boolean): OpdBillItem[] {
  const byId = new Map((quote?.items ?? []).map((i) => [i.id, i]));
  return lines
    .map((l) => {
      const item = l.itemId != null ? byId.get(l.itemId) : undefined;
      return {
        description: l.description.trim() || item?.name || 'Other charge',
        unit_price: Number(l.fee) || 0,
        hsn_code: item?.hsn_code || '999319',
        gst_percent: gstEnabled ? l.gst : 0,
        discount_percent: Number(l.discount) || 0,
        quantity: 1,
        ...(item ? { opd_billing_item_id: item.id } : {}),
      };
    })
    .filter((i) => i.unit_price > 0);
}

/** Mirrors backend/utils/billingPricing.js: gross → discount → net → GST. */
export function billTotals(items: OpdBillItem[]) {
  const subtotal = items.reduce((s, i) => s + i.unit_price, 0);
  const discount = items.reduce((s, i) => s + i.unit_price * (i.discount_percent / 100), 0);
  const gst = items.reduce((s, i) => s + i.unit_price * (1 - i.discount_percent / 100) * (i.gst_percent / 100), 0);
  return { subtotal, discount, gst, total: subtotal - discount + gst };
}

/** Lines a biller without fee-override has pushed past what they may do. */
export function lineProblems(lines: OpdLine[], quote: OpdFeeQuote | undefined): string[] {
  if (!quote || quote.can_override) return [];
  const byId = new Map(quote.items.map((i) => [i.id, i]));
  const out: string[] = [];
  for (const l of lines) {
    const item = l.itemId != null ? byId.get(l.itemId) : undefined;
    if (!item) { out.push(`${l.description || 'A charge'} is not on the fee schedule`); continue; }
    if ((Number(l.discount) || 0) > item.max_discount_percent) {
      out.push(`${item.name}: discount can be at most ${item.max_discount_percent}%`);
    }
  }
  return out;
}

